import type { ReactNode } from 'react';
import { formatCurrency } from '../../shared/format_currency.js';
import { useCheckoutEnv } from '../drawer/env_context.js';
import { summaryTraveler } from '../drawer/trip_summary_panel.js';
import { useCheckoutTripServices } from '../form/use_trip_services.js';
import { useTravelerPhotos } from '../form/use_traveler_photos.js';
import type { CheckoutTravelerRef, ConciergeRequestSource } from '../model/checkout_source.js';
import { RequestTripSummary } from '../request/request_trip_summary.js';
import {
  conciergeRequestCity,
  conciergeRequestDates,
  conciergeRequestRowItems,
  conciergeRequestTotal,
} from './concierge_request_rows.js';

export interface ConciergeSummaryPanelProps {
  request: ConciergeRequestSource;
  travelers: CheckoutTravelerRef[];
  tripId?: string;
  submitting: boolean;
  canSubmit: boolean;
  submitNotice?: { text: string };
  onSubmit: () => void;
  /** Saída secundária do rodapé no touch — o ✕ do topbar fica longe do polegar. */
  onBack: () => void;
}

/**
 * O cartão "Sua viagem" do pedido OFFLINE: o `RequestTripSummary` com o que a viagem já tem e as linhas do pedido — as mesmas
 * de `concierge_request_rows.tsx`, que a tela "Solicitação enviada" do host também
 * desenha. No voo, a ida já escolhida aparece como referência guardada.
 */
export function ConciergeSummaryPanel({
  request,
  travelers,
  tripId,
  submitting,
  canSubmit,
  submitNotice,
  onSubmit,
  onBack,
}: Readonly<ConciergeSummaryPanelProps>): ReactNode {
  const { io, copy, locale, cityOf } = useCheckoutEnv();
  const { trip, concierge } = copy;
  const booked = useCheckoutTripServices(io, tripId);
  const city = conciergeRequestCity(request, cityOf);
  const dates = conciergeRequestDates(request, locale);
  const firstId = travelers[0]?.id;
  const photos = useTravelerPhotos(io, firstId ? [firstId] : []);
  const traveler = summaryTraveler(travelers, photos, trip);
  return (
    <RequestTripSummary
      {...(city ? { title: trip.title(city) } : {})}
      {...(dates ? { dates } : {})}
      {...(traveler ? { traveler } : {})}
      booked={booked.map((service) => ({
        id: service.id,
        title: service.title,
        ...(service.statusLabel ? { status: service.statusLabel } : {}),
        ...(service.price
          ? { price: formatCurrency(service.price.amount, service.price.currency, locale) }
          : {}),
      }))}
      rows={conciergeRequestRowItems(request, concierge, locale)}
      total={conciergeRequestTotal(request, concierge)}
      note={concierge.panel.note[request.productType]}
      submitting={submitting}
      canSubmit={canSubmit}
      {...(submitNotice ? { notice: submitNotice.text } : {})}
      onSubmit={onSubmit}
      onBack={onBack}
      copy={{
        kicker: trip.sectionTitle,
        status: concierge.panel.inProgress,
        totalLabel: concierge.panel.totalLabel,
        submit: concierge.submit,
        submitting: copy.price.submitting,
        back: concierge.back,
      }}
    />
  );
}
